import { getBackendClient } from "@/lib/backend";

const closing = new Map<string, Promise<void>>();
const writing = new Map<string, Promise<void>>();
// How long a close waits for writes the conversation must still accept.
const WRITE_GRACE_MS = 3_000;

/**
 * Holds the conversation's revocation until `writes` settle (within a short
 * grace), so voice turns finalized before a call ends are stored while the
 * conversation still accepts them. `writes` must never reject.
 */
export function holdConversationClose(conversationId: string, writes: Promise<void>): void {
  writing.set(conversationId, writes);
  void writes.then(() => {
    if (writing.get(conversationId) === writes) writing.delete(conversationId);
  });
}

function writesSettled(writes: Promise<void>): Promise<void> {
  let timer: ReturnType<typeof setTimeout> | undefined;
  const grace = new Promise<void>((resolve) => {
    timer = setTimeout(resolve, WRITE_GRACE_MS);
  });
  return Promise.race([writes, grace]).finally(() => clearTimeout(timer));
}

/** The transport releases media immediately; the coordinator awaits this same revocation. */
export function closeAssistantConversation(conversationId: string): Promise<void> {
  const existing = closing.get(conversationId);
  if (existing) return existing;
  const send = () =>
    getBackendClient().functions.invoke<{ ok: boolean }>("assistant-session-close", {
      body: { conversationId },
      method: "POST",
    });
  const writes = writing.get(conversationId);
  const request = (writes ? writesSettled(writes).then(send) : send()).then(({ data, error }) => {
    if (error || data?.ok !== true)
      throw new Error("The previous conversation could not be closed. Please retry.");
  });
  let timer: ReturnType<typeof setTimeout> | undefined;
  const promise = Promise.race([
    request,
    new Promise<never>((_, reject) => {
      timer = setTimeout(
        () => reject(new Error("The previous conversation could not be closed. Please retry.")),
        10_000,
      );
    }),
  ]).finally(() => {
    clearTimeout(timer);
    if (closing.get(conversationId) === promise) closing.delete(conversationId);
  });
  closing.set(conversationId, promise);
  return promise;
}
